import { FormattedMessage } from 'react-intl';
import { Localized, PoolButton, PoolTypography, useFilter } from '@synergy/components';
import { PaloColumn, PaloListView } from '@synergy/components-palo';
import { Stack, styled } from '@mui/material';
import { defaultPageSize } from '@synergy/config';
import { makeStyles } from '@mui/styles';
import { useSearchParams } from 'react-router-dom';

import { ImportItem } from '../../models/import-item';
import { getImportStatusIcon } from '../../utils/get-import-status-icon';
import { useAppSelector } from '../../../../../store/use-appstate-selector';

const useStyles = makeStyles(() => ({
    statusIcon: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    allDone: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
    },
}));

const StyledList = styled('ul')(({ theme }) => ({
    paddingLeft: theme.spacing(2.5),
    margin: 0,
}));

type ImportItemErrorFields = Pick<ImportItem, 'errors' | 'warnings'>;

type ImportDetailErrorTableProps = {
    field: keyof ImportItemErrorFields;
    label: string;
};

export const ImportDetailErrorTable = ({ field, label }: ImportDetailErrorTableProps) => {
    const classes = useStyles();
    const [searchParams, setSearchParams] = useSearchParams();
    const { filter, reset: resetFilter } = useFilter();
    const { importJobItems } = useAppSelector(state => state.import);

    const columns: PaloColumn<ImportItem>[] = [
        {
            key: 'status',
            type: 'icon',
            head: <FormattedMessage id="app.table.column.status" />,
            cell: item => <div className={classes.statusIcon}>{getImportStatusIcon(item.changeState)}</div>,
        },
        {
            key: 'number',
            type: 'articleNumber',
            head: <FormattedMessage id="app.table.column.articleNumber" />,
            cell: item => item.number,
        },
        {
            key: 'articleName',
            type: 'name',
            head: <FormattedMessage id="app.table.column.articleName" />,
            cell: item => <Localized value={item.name} />,
        },
        {
            key: 'errors',
            type: 'name',
            wrap: true,
            head: <FormattedMessage id={label} />,
            cell: item => (
                <Stack gap={1}>
                    <StyledList>
                        {item[field]?.map((error, index) => (
                            <li key={index}>{error}</li>
                        ))}
                    </StyledList>
                </Stack>
            ),
        },
    ];

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    if (importJobItems.items.length > 0) {
        return (
            <PaloListView<ImportItem, number>
                embedded={true}
                columns={columns}
                items={importJobItems.items}
                getRowId={item => item.id}
                emptyLabel={<FormattedMessage id="app.common.emptyResult" />}
                pagination={{
                    count: importJobItems.total,
                    page: importJobItems.page,
                    limit: importJobItems.limit,
                    onPageChange: page => updateParams({ page: String(page) }),
                    onLimitChange: limit => updateParams({ limit: String(limit || defaultPageSize), page: '1' }),
                }}
            />
        );
    }

    if (Object.entries(filter).length > 0 && importJobItems.items.length === 0) {
        return (
            <>
                <PoolTypography id="app.common.emptyResult" />
                <br />
                <PoolButton styleType="firstLevel" onClick={resetFilter}>
                    <FormattedMessage id="app.filter.reset" />
                </PoolButton>{' '}
            </>
        );
    }

    return (
        <div className={classes.allDone}>
            <PoolTypography typography="h5" id="app.imports.detail.noArticles" style={{ marginBottom: 8 }} />
        </div>
    );
};
